import { useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { AccountStates } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useInnerWorkflow } from '@ovComponents/4-module/workflowCompletion/components/useInnerWorkflow';
import type { WorkflowCompletionWithId } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionWithId.types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useGlobalToast } from '@providers/globalToastProvider';
import { groupBy, uniq } from 'lodash-es';
import { type ComponentProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountCard } from './accountCard';
import { FETCH_ACCOUNTS_FOR_REVIEW } from './reviewAccount.queries';

export type ReviewAccountSubstepOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  createAnotherAccount?: boolean;
  enableAccountEdit?: boolean;
  enableAccountDelete?: boolean;
  accountEditWorkflow?: 'default' | string;
  displayAccountType?: boolean;
  displayCustodian?: boolean;
  displayRepcode?: boolean;
  displayRiskTolerence?: boolean;
  displayAffiliates?: boolean;
  displayLinkedGoal?: boolean;
  displayCustomFields?: boolean;
  enableCallToActions?: boolean;
  callToActions?: unknown[];
};

export const ReviewAccountVisual = ({
  options,
  userId,
  workflowCompletion,
}: {
  options: ReviewAccountSubstepOptions;
  userId?: string;
  workflowCompletion: WorkflowCompletionWithId;
}) => {
  const { workflowData } = useContext(WorkflowContext);
  const { data, loading, refetch } = useQuery(
    FETCH_ACCOUNTS_FOR_REVIEW as any,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          pagination: { perPage: 100 },
          filter: {
            userId: workflowData.currentUserId || userId,
            states: [
              AccountStates.Active,
              AccountStates.Requested,
              AccountStates.Frozen,
              AccountStates.Initiated,
              AccountStates.Ready,
            ],
          },
        },
      },
    },
  );

  const { t } = useTranslation([
    'workflowCompletions',
    'accountTypes',
    'affiliationTypes',
  ]);

  const { createWorkflowCompletion, innerWorkflowModal } = useInnerWorkflow({
    refetch,
    workflowCompletion,
  });
  const currentUserId = workflowData.currentUserId || userId;
  const { showToast } = useGlobalToast();
  const onAccountEdit: ComponentProps<typeof AccountCard>['onAccountEdit'] =
    async (accountId) => {
      if (
        !options.accountEditWorkflow ||
        options.accountEditWorkflow === 'default'
      ) {
        showToast({
          message: t('reviewAccount.noWorkflowErrorMessage'),
          severity: 'error',
        });
        return;
      }
      await createWorkflowCompletion({
        currentUserId,
        workflowId: options.accountEditWorkflow,
        workflowContext: {
          ...workflowData,
          currentAccountId: accountId,
        },
      });
    };

  const accountsById = groupBy(data?.fetchAccounts?.accounts, 'id');

  const accountsFromWorkflow = uniq([
    workflowData?.currentAccountId,
    ...(workflowData?.createdAccounts?.map((a) => a.id) || []),
  ])
    .map((id: string | undefined) => accountsById[id || '']?.[0])
    .filter((account) => !!account);

  const accountsToDisplay = accountsFromWorkflow.length
    ? accountsFromWorkflow
    : (data?.fetchAccounts?.accounts ?? []);

  return (
    <Box>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackend(options?.subtitle)}
      </Typography>

      {loading ? (
        <>
          <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
        </>
      ) : (
        accountsToDisplay?.map((account: any) => (
          <AccountCard
            key={account.id}
            account={account}
            allAccounts={accountsToDisplay}
            options={options}
            onAccountDeleted={() => refetch()}
            onAccountEdit={onAccountEdit}
          />
        ))
      )}
      {innerWorkflowModal}
    </Box>
  );
};
